import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Switch, Table, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useLeave } from '../../hooks/useLeave';
import { LeaveType } from '../../types';

const LeaveTypesConfigView: React.FC = () => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<LeaveType | null>(null);
  const { leaveTypes, fetchLeaveTypes, createLeaveType, updateLeaveType, deleteLeaveType } = useLeave();

  useEffect(() => { fetchLeaveTypes(); }, []);

  const handleOpen = (item?: LeaveType) => {
    form.resetFields();
    if (item) form.setFieldsValue(item);
    setEditItem(item ?? null);
    setOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    if (editItem?.id) {
      await updateLeaveType(editItem.id, values);
    } else {
      await createLeaveType(values);
    }
    setOpen(false);
  };

  const columns = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 80 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Entitlement Days', dataIndex: 'entitlementDays', key: 'entitlementDays', width: 120 },
    { title: 'Carry Over', dataIndex: 'carryOverDays', key: 'carryOverDays', width: 100 },
    {
      title: 'Encashable', dataIndex: 'encashable', key: 'encashable', width: 100,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Active', dataIndex: 'active', key: 'active', width: 70,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 100,
      render: (_: any, record: LeaveType) => (
        <span>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleOpen(record)} style={{ marginRight: 4 }} />
          <Popconfirm title="Delete this leave type?" onConfirm={() => deleteLeaveType(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card
        title="Leave Types Configuration"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpen()}>Add Leave Type</Button>}
      >
        <Table dataSource={leaveTypes} columns={columns} rowKey="id" size="small" pagination={false} />
      </Card>

      <Modal
        title={editItem ? 'Edit Leave Type' : 'New Leave Type'}
        open={open}
        onOk={handleSave}
        onCancel={() => setOpen(false)}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. ANNUAL" maxLength={20} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Annual Leave" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="entitlementDays" label="Entitlement Days" rules={[{ required: true }]}>
            <InputNumber min={0} max={365} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="carryOverDays" label="Carry Over Days" initialValue={0}>
            <InputNumber min={0} max={365} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="encashable" label="Encashable" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="requiresDocumentation" label="Requires Documentation" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LeaveTypesConfigView;
